import { colors } from 'styles/tokens'

/**
 * Colorblind-safe categorical palette for the reporting charts (multi-series
 * lines, stacked/grouped bars, combo). Scoped to this prototype — it replaces
 * the app-wide `chartTheme.categorical` *here only*; the homepage widgets keep
 * the brand rainbow, so the blast radius stays inside reporting.
 *
 * Why replace it: the default palette pairs brand green with red-500 and
 * amber-500, which collapse into one another under deuteranopia/protanopia
 * (the most common color-vision deficiency) — the worst case for a chart whose
 * whole job is telling reps apart.
 *
 * This is the Okabe-Ito palette (the de-facto standard colorblind-safe
 * categorical set, engineered to stay distinct across all three common CVD
 * types), led by First Bite's brand green so the charts still read as ours.
 * Two Okabe-Ito members are dropped for failing the 3:1 contrast floor (rule
 * 16) on the off-white card: pale yellow (#F0E442) and pale sky blue (#56B4E9,
 * only 2.31:1). Sky blue is replaced with a dark cyan (#00838F, 4.52:1) that
 * stays distinct from the medium blue and the brand green. A neutral gray
 * rounds out the 7th slot; past that the index cycles.
 *
 * Tufte note: color here is a *secondary* cue — every line is labeled directly
 * at its right end, so the palette only has to separate adjacent series, not
 * carry identity on its own.
 */
export const SERIES_PALETTE = [
  colors['brand-bright'], // #0A9150 — First Bite brand green
  '#0072B2', // blue
  '#E69F00', // orange
  '#CC79A7', // reddish purple
  '#00838F', // dark cyan (replaces Okabe-Ito sky blue; passes 3:1 on the card)
  '#D55E00', // vermillion
  '#555555', // neutral gray
] as const

/** Series color for index `i`, cycling once the palette is exhausted. */
export const seriesColor = (i: number): string =>
  SERIES_PALETTE[i % SERIES_PALETTE.length]
